import React, { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Clock, MapPin, Printer } from 'lucide-react';
import { Button } from '../ui/button';
import { MapLink } from '../ui/MapLink';
import { MemberQuickNav } from '../MemberQuickNav';
import api from '../../lib/api';
import { events as mockEvents } from '../../data/mock';

const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const chipColor = (e) =>
  e.tentative
    ? 'bg-gray-400 text-white'
    : 'bg-[#bd8cae] text-white';

export default function MemberCalendarPage() {
  const [events, setEvents] = useState([]);
  const today = new Date();
  const [view, setView] = useState({ year: today.getFullYear(), month: today.getMonth() });

  useEffect(() => {
    api.get('/events').then((res) => setEvents(res.data)).catch(() => setEvents(mockEvents));
  }, []);

  useEffect(() => {
    if (!events.length) return;
    const next = events
      .map((e) => new Date(`${e.date}T23:59:59`))
      .filter((d) => d >= new Date())
      .sort((a, b) => a - b)[0];
    if (next) setView({ year: next.getFullYear(), month: next.getMonth() });
  }, [events]);

  const eventsByDay = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      const d = new Date(`${e.date}T12:00:00`);
      if (d.getFullYear() === view.year && d.getMonth() === view.month) {
        (map[d.getDate()] = map[d.getDate()] || []).push(e);
      }
    });
    return map;
  }, [events, view]);

  const firstDay = new Date(view.year, view.month, 1).getDay();
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
  const cells = [...Array(firstDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  while (cells.length % 7 !== 0) cells.push(null);

  const monthName = new Date(view.year, view.month, 1).toLocaleString('default', { month: 'long' });
  const shift = (delta) => {
    const d = new Date(view.year, view.month + delta, 1);
    setView({ year: d.getFullYear(), month: d.getMonth() });
  };
  const isToday = (day) =>
    day === today.getDate() && view.month === today.getMonth() && view.year === today.getFullYear();
  const isPastDay = (day) => new Date(view.year, view.month, day, 23, 59) < today;

  const upcoming = events
    .filter((e) => new Date(`${e.date}T23:59:59`) >= today)
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 12);

  return (
    <main className="min-h-screen bg-[#f1e3eb] pt-44 pb-20 px-4" data-testid="member-calendar-page">
      <div className="max-w-5xl mx-auto">
        <MemberQuickNav />
        <div className="text-center mb-10">
          <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">Members Only</span>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#3c4a2a] mt-2">Club Calendar</h1>
          <Button
            onClick={() => window.print()}
            variant="outline"
            data-testid="calendar-print-btn"
            className="mt-5 border-[#5d6b3c] text-[#5d6b3c] hover:bg-[#5d6b3c] hover:text-white print:hidden"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print This Month
          </Button>
        </div>

        {/* Spiral binding */}
        <div className="relative" id="printable-calendar">
          <div className="absolute -top-3 left-0 right-0 flex justify-evenly px-8 z-10">
            {Array.from({ length: 12 }).map((_, i) => (
              <span key={i} className="w-4 h-6 rounded-full border-[3px] border-[#8a8a8a] bg-[#f1e3eb] shadow-sm" />
            ))}
          </div>

          <div className="bg-white rounded-xl shadow-2xl border border-[#3c4a2a]/10 overflow-hidden pt-6">
            {/* Month header */}
            <div className="flex items-center justify-between px-6 sm:px-10 py-5 border-b-2 border-[#5d6b3c]/20">
              <Button variant="ghost" size="icon" onClick={() => shift(-1)} data-testid="calendar-prev-month" aria-label="Previous month">
                <ChevronLeft className="w-6 h-6 text-[#5d6b3c]" />
              </Button>
              <div className="text-center">
                <h2 className="font-serif text-3xl sm:text-4xl text-[#3c4a2a]" data-testid="calendar-month-label">
                  {monthName} <span className="text-[#bd8cae]">{view.year}</span>
                </h2>
                <p className="text-xs uppercase tracking-widest text-[#3c4a2a]/50 mt-1">The Women's Club of Lincoln County</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => shift(1)} data-testid="calendar-next-month" aria-label="Next month">
                <ChevronRight className="w-6 h-6 text-[#5d6b3c]" />
              </Button>
            </div>

            {/* Weekday row */}
            <div className="grid grid-cols-7 bg-[#5d6b3c] text-white text-center text-[10px] sm:text-xs tracking-widest">
              {WEEKDAYS.map((d) => (
                <div key={d} className="py-2 font-medium">{d}</div>
              ))}
            </div>

            {/* Day grid */}
            <div className="grid grid-cols-7" data-testid="calendar-grid">
              {cells.map((day, i) => (
                <div
                  key={i}
                  className={`min-h-[70px] sm:min-h-[110px] border border-[#3c4a2a]/10 p-1.5 sm:p-2 ${
                    day === null ? 'bg-[#f1e3eb]/40' : isPastDay(day) ? 'bg-white/60' : 'bg-white'
                  }`}
                >
                  {day && (
                    <>
                      <span
                        className={`inline-flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 text-xs sm:text-sm rounded-full ${
                          isToday(day)
                            ? 'bg-[#bd8cae] text-white font-bold'
                            : isPastDay(day)
                            ? 'text-[#3c4a2a]/35'
                            : 'text-[#3c4a2a]'
                        }`}
                      >
                        {day}
                      </span>
                      <div className="mt-1 space-y-1">
                        {(eventsByDay[day] || []).map((e) => (
                          <div
                            key={e.id}
                            title={`${e.title} — ${e.time} — ${e.location}`}
                            data-testid={`calendar-event-${e.id}`}
                            className={`${chipColor(e)} ${isPastDay(day) ? 'opacity-50' : ''} rounded px-1.5 py-1 text-[9px] sm:text-[11px] leading-tight`}
                          >
                            <p className="font-medium truncate">{e.title}</p>
                            <p className="opacity-90 hidden sm:block">{e.time}</p>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Legend */}
            <div className="flex items-center justify-center gap-6 py-4 text-xs text-[#3c4a2a]/70 border-t border-[#3c4a2a]/10">
              <span className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-[#bd8cae]" /> Club Event</span>
              <span className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-gray-400" /> Community (tentative)</span>
            </div>
          </div>
        </div>

        {/* Upcoming list */}
        <div className="mt-12">
          <h3 className="font-serif text-2xl text-[#3c4a2a] mb-5">Upcoming Meetings & Events</h3>
          {upcoming.length === 0 ? (
            <p className="text-[#3c4a2a]/60" data-testid="calendar-no-upcoming">No upcoming events on the calendar yet.</p>
          ) : (
            <div className="space-y-3">
              {upcoming.map((e) => {
                const d = new Date(`${e.date}T12:00:00`);
                return (
                  <div key={e.id} data-testid={`calendar-upcoming-${e.id}`} className="flex items-center gap-4 bg-white rounded-lg shadow-sm border border-[#bd8cae]/20 p-4">
                    <div className={`w-14 h-14 rounded-lg text-white flex flex-col items-center justify-center flex-shrink-0 ${e.tentative ? 'bg-gray-400' : 'bg-[#bd8cae]'}`}>
                      <span className="text-lg font-bold leading-none">{d.getDate()}</span>
                      <span className="text-[10px] uppercase">{d.toLocaleString('default', { month: 'short' })}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-[#3c4a2a]">{e.title}</p>
                      <p className="text-sm text-[#3c4a2a]/60 flex flex-wrap items-center gap-x-3">
                        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{e.time}</span>
                        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 flex-shrink-0" /><MapLink location={e.location} /></span>
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
